import React from 'react';
import { useQuery } from '@apollo/client';
import {
  AddToBagRecommendationsContextProvider,
  CategoryRecommendationsContextProvider,
  GenericRecommendationsContextProvider,
  MyListRecommendationsContextProvider,
  NullSearchRecommendationsContextProvider,
  PDPRecommendationsContextProvider,
  PickedForYouRecommendationsContextProvider,
  QuickviewRecommendationsContextProvider,
  SearchRecommendationsContextProvider,
  useDatabridgeSubscribe,
} from '@xp-utilities/web';
import {
  RecommendationsWrapperPropTypes,
  RecommendationsWrapperDefaultProps,
} from './props';
import { PAGE_KINDS } from '../../tools/constants';
import { PDP_RECOMMENDATIONS_QUERY } from './operations';

export const INSTANCE_DATA_ATTRIBUTE = 'data-recommendations-client';

function PDPRecommendationsContextProviderWrapper(props) {
  const { data } = useQuery(PDP_RECOMMENDATIONS_QUERY, {
    ssr: false,
    context: { batch: true },
    fetchPolicy: 'cache-and-network',
  });

  if (!data) return null;

  // Block if PDP is disabled
  const { isPDPRecommendationsEnabled } = data;
  if (!isPDPRecommendationsEnabled) return null;

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <PDPRecommendationsContextProvider {...props} />;
}

// Context Provider Mapping
const contextProviderMap = {
  'quickview-1': QuickviewRecommendationsContextProvider,
  'addToBag-2': AddToBagRecommendationsContextProvider,
  'null-search-1': NullSearchRecommendationsContextProvider,
  'search-1': SearchRecommendationsContextProvider,
  'account-flyout-1': GenericRecommendationsContextProvider,
};

// Context Providers for specific page kinds
const pageKindContextProviders = {
  [PAGE_KINDS.pickedForYou]: PickedForYouRecommendationsContextProvider,
  [PAGE_KINDS.category]: CategoryRecommendationsContextProvider,
  [PAGE_KINDS.myList]: MyListRecommendationsContextProvider,
  [PAGE_KINDS.product]: PDPRecommendationsContextProviderWrapper,
};

function renderWithContextProvider(ContextProvider, clientId, children) {
  return (
    <ContextProvider id={clientId}>
      <div
        id={clientId}
        data-testid={clientId}
        {...{ [INSTANCE_DATA_ATTRIBUTE]: clientId }}
      >
        {children}
      </div>
    </ContextProvider>
  );
}

export default function RecommendationsWrapper({ children, clientId }) {
  const pageKind = useDatabridgeSubscribe('page.pageKind');

  // Directly map clientId to a context provider if applicable
  if (contextProviderMap[clientId]) {
    const ContextProvider = contextProviderMap[clientId];
    return renderWithContextProvider(ContextProvider, clientId, children);
  }

  // Handle page kind-based context providers
  const PageKindContextProvider = pageKindContextProviders[pageKind];

  if (PageKindContextProvider) return renderWithContextProvider(PageKindContextProvider, clientId, children);

  // Default fallback context provider
  return renderWithContextProvider(GenericRecommendationsContextProvider, clientId, children);
}

RecommendationsWrapper.propTypes = RecommendationsWrapperPropTypes;
RecommendationsWrapper.defaultProps = RecommendationsWrapperDefaultProps;
